'use client';
import React, { useState } from 'react';
import { Bell, ChevronDown, ChevronUp, Loader2, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

interface AlertForm {
  email: string;
  direction: 'BUY' | 'SELL' | 'ANY';
  minAiScore: number;
  minConfidence: number;
  timeWindowStart: string;
  timeWindowEnd: string;
  timezone: string;
}

const TIMEZONES = [
  { label: 'UTC', value: 'UTC' },
  { label: 'EST (UTC-5)', value: 'America/New_York' },
  { label: 'CST (UTC-6)', value: 'America/Chicago' },
  { label: 'MST (UTC-7)', value: 'America/Denver' },
  { label: 'PST (UTC-8)', value: 'America/Los_Angeles' },
  { label: 'GMT (UTC+0)', value: 'Europe/London' },
  { label: 'CET (UTC+1)', value: 'Europe/Paris' },
  { label: 'EET (UTC+2)', value: 'Europe/Helsinki' },
  { label: 'GST (UTC+4)', value: 'Asia/Dubai' },
  { label: 'IST (UTC+5:30)', value: 'Asia/Kolkata' },
  { label: 'SGT (UTC+8)', value: 'Asia/Singapore' },
  { label: 'JST (UTC+9)', value: 'Asia/Tokyo' },
  { label: 'AEST (UTC+10)', value: 'Australia/Sydney' },
];

const DEFAULT_FORM: AlertForm = {
  email: '',
  direction: 'ANY',
  minAiScore: 70,
  minConfidence: 65,
  timeWindowStart: '09:00',
  timeWindowEnd: '17:00',
  timezone: 'UTC',
};

export default function SetSignalAlert() {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<AlertForm>(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Please enter a valid email address', { duration: 3000 });
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch('/api/signal-alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim(),
          direction: form.direction,
          min_ai_score: form.minAiScore,
          min_confidence: form.minConfidence,
          time_window_start: form.timeWindowStart,
          time_window_end: form.timeWindowEnd,
          timezone: form.timezone,
        }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to save alert');

      setSaved(true);
      toast.success('Alert saved! You will be emailed when a matching signal is generated.', {
        duration: 4000,
      });
    } catch (err: any) {
      toast.error(err.message || 'Failed to save alert', { duration: 3000 });
    } finally {
      setIsSaving(false);
    }
  };

  const directionOptions: { value: AlertForm['direction']; label: string; color: string }[] = [
    { value: 'ANY', label: 'Any', color: 'text-zinc-300' },
    { value: 'BUY', label: 'BUY', color: 'text-emerald-400' },
    { value: 'SELL', label: 'SELL', color: 'text-red-400' },
  ];

  return (
    <div className="rounded-xl border border-white/5 bg-zinc-900/40 overflow-hidden">
      {/* Header toggle */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-800/40 transition-colors duration-150"
      >
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <Bell className="w-3.5 h-3.5 text-amber-400" />
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-zinc-200">Set Signal Alert</p>
            <p className="text-xs text-zinc-500">
              {saved
                ? `Active · ${form.direction === 'ANY' ? 'Any direction' : form.direction} · Score ≥${form.minAiScore}`
                : 'Get emailed when a matching signal is generated'}
            </p>
          </div>
          {saved && (
            <span className="ml-1 flex items-center gap-1 text-[10px] font-medium text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 rounded-full px-2 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Active
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        )}
      </button>

      {/* Collapsible body */}
      {isOpen && (
        <div className="border-t border-white/5 px-4 py-4 space-y-4">
          {/* Email input */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Your Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="you@example.com"
              className="w-full bg-zinc-800/80 border border-white/5 rounded-lg px-3 py-2 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-amber-500/40"
            />
            <p className="text-[10px] text-zinc-600">We'll send signal alerts to this email.</p>
          </div>

          {/* Direction */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Direction</label>
            <div className="flex gap-2">
              {directionOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setForm((f) => ({ ...f, direction: opt.value }))}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-all duration-150 ${
                    form.direction === opt.value
                      ? 'border-amber-500/50 bg-amber-500/10 text-amber-300' :'border-white/5 bg-zinc-800/60 text-zinc-400 hover:border-white/10 hover:text-zinc-300'
                  }`}
                >
                  <span className={form.direction === opt.value ? 'text-amber-300' : opt.color}>
                    {opt.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Min AI Score */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Min AI Score</label>
              <span className="text-xs font-mono text-amber-400">{form.minAiScore}/100</span>
            </div>
            <input
              type="range"
              min={40}
              max={95}
              step={5}
              value={form.minAiScore}
              onChange={(e) => setForm((f) => ({ ...f, minAiScore: Number(e.target.value) }))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-amber-400 bg-zinc-700"
            />
            <div className="flex justify-between text-[10px] text-zinc-600 font-mono">
              <span>40</span>
              <span>95</span>
            </div>
          </div>

          {/* Min Confidence */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Min Confidence</label>
              <span className="text-xs font-mono text-amber-400">{form.minConfidence}%</span>
            </div>
            <input
              type="range"
              min={40}
              max={95}
              step={5}
              value={form.minConfidence}
              onChange={(e) => setForm((f) => ({ ...f, minConfidence: Number(e.target.value) }))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-amber-400 bg-zinc-700"
            />
            <div className="flex justify-between text-[10px] text-zinc-600 font-mono">
              <span>40</span>
              <span>95</span>
            </div>
          </div>

          {/* Time Window */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              Time Window
            </label>
            <div className="flex items-center gap-2">
              <input
                type="time"
                value={form.timeWindowStart}
                onChange={(e) => setForm((f) => ({ ...f, timeWindowStart: e.target.value }))}
                className="flex-1 bg-zinc-800/80 border border-white/5 rounded-lg px-3 py-2 text-xs text-zinc-200 font-mono focus:outline-none focus:border-amber-500/40"
              />
              <span className="text-xs text-zinc-500">to</span>
              <input
                type="time"
                value={form.timeWindowEnd}
                onChange={(e) => setForm((f) => ({ ...f, timeWindowEnd: e.target.value }))}
                className="flex-1 bg-zinc-800/80 border border-white/5 rounded-lg px-3 py-2 text-xs text-zinc-200 font-mono focus:outline-none focus:border-amber-500/40"
              />
            </div>
          </div>

          {/* Timezone */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Timezone</label>
            <select
              value={form.timezone}
              onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
              className="w-full bg-zinc-800/80 border border-white/5 rounded-lg px-3 py-2 text-xs text-zinc-200 focus:outline-none focus:border-amber-500/40 appearance-none cursor-pointer"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          {/* Summary preview */}
          <div className="rounded-lg bg-zinc-800/50 border border-white/5 px-3 py-2.5 text-xs text-zinc-400 font-mono leading-relaxed">
            Alert:{' '}
            <span className={form.direction === 'BUY' ? 'text-emerald-400' : form.direction === 'SELL' ? 'text-red-400' : 'text-zinc-300'}>
              {form.direction}
            </span>{' '}
            · Score ≥<span className="text-amber-400">{form.minAiScore}</span> · Confidence ≥
            <span className="text-amber-400">{form.minConfidence}%</span> · {form.timeWindowStart}–{form.timeWindowEnd}{' '}
            <span className="text-zinc-500">{form.timezone}</span>
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-semibold text-zinc-950 transition-all duration-150 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F5D76E 50%, #B8961F 100%)' }}
          >
            {isSaving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <CheckCircle className="w-3.5 h-3.5" />
            )}
            {saved ? 'Update Alert' : 'Save Alert'}
          </button>

          <p className="text-[10px] text-zinc-600 text-center leading-relaxed">
            Alert expires after 7 days — renew anytime.
          </p>
        </div>
      )}
    </div>
  );
}
